import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { Home, Sprout, Layers, ScrollText, ShieldAlert, Gavel, Landmark, FileText, PenTool, GraduationCap, Map as MapIcon, Users, DatabaseBackup, ClipboardCheck, LogOut, Link2, Sparkles, X, MessageSquare, Percent, Search, Newspaper, CalendarDays, User, Banknote, Radar, LineChart, BookOpen, Receipt, Network, HelpCircle, Scale, Users2, Globe, Home as HomeIcon, Mail, HeartPulse, Flower2, Phone, FolderLock, Bell, ShieldCheck, ListChecks, Wallet, Building2, CalendarClock, Clock, PenLine, Menu, Image as ImageIcon, FlaskConical, ShieldQuestion, Bot, Gauge, LifeBuoy, Compass } from "lucide-react";
import { me, setUiMode as apiSetUiMode } from "../lib/api";
import AiPanel from "./AiPanel";

const groups = [
  { label: "Start", items: [
    { to: "/me", icon: User, label: "Your Root (personal)" },
    { to: "/dashboard", icon: Home, label: "Council Dashboard" },
  ]},
  { label: "The Grove", items: [
    { to: "/my-root", icon: Sprout, label: "My Root (private)" },
    { to: "/structure", icon: Layers, label: "Structure" },
    { to: "/apex-structure", icon: Network, label: "Apex Structure" },
    { to: "/tax-classification", icon: Scale, label: "Entity & Tax" },
    { to: "/entities", icon: Layers, label: "Entities" },
    { to: "/family-office", icon: Users, label: "Family Office" },
  ]},
  { label: "Council Room", scope: "collective", items: [
    { to: "/council", icon: MessageSquare, label: "Messages" },
  ]},
  { label: "Registers", items: [
    { to: "/instruments", icon: ScrollText, label: "Instruments" },
    { to: "/compliance", icon: ShieldAlert, label: "Compliance" },
    { to: "/resolutions", icon: Gavel, label: "Resolutions" },
    { to: "/policy-bank", icon: Banknote, label: "Policy Bank (IBC)" },
    { to: "/beneficiaries", icon: Users, label: "Beneficiaries" },
    { to: "/estate-drafting", icon: PenTool, label: "Estate Drafting" },
    { to: "/creditor-remedies", icon: ShieldAlert, label: "Charging Orders" },
    { to: "/insurance-loans", icon: FileText, label: "Insurance & Loans" },
  ]},
  { label: "Legacy Vault", items: [
    { to: "/vault", icon: FolderLock, label: "Secure Document Vault" },
    { to: "/media", icon: ImageIcon, label: "Media Library" },
    { to: "/people", icon: Users2, label: "Beneficiaries & Executors" },
    { to: "/digital-assets", icon: Globe, label: "Digital Assets" },
    { to: "/physical-assets", icon: HomeIcon, label: "Physical Assets" },
    { to: "/legacy-messages", icon: Mail, label: "Legacy Messages" },
    { to: "/healthcare-directives", icon: HeartPulse, label: "Healthcare Directives" },
    { to: "/final-wishes", icon: Flower2, label: "Funeral & Final Wishes" },
    { to: "/emergency-contacts", icon: Phone, label: "Emergency Contacts" },
    { to: "/activation", icon: ShieldCheck, label: "Trust Activation" },
  ] },
  { label: "Alerts & Audit", items: [
    { to: "/notifications", icon: Bell, label: "Notifications" },
    { to: "/reminders", icon: CalendarDays, label: "Reminders" },
    { to: "/audit", icon: ListChecks, label: "Audit Trail" },
  ] },
  { label: "Governance & Safety", items: [
    { to: "/safety", icon: FlaskConical, label: "Safe Simulation & Provenance" },
    { to: "/disputes", icon: ShieldQuestion, label: "Conflict & Dispute Register" },
    { to: "/sub-agents", icon: Bot, label: "Sub-Agent Console" },
    { to: "/continuity", icon: LifeBuoy, label: "Continuity & Break-Glass" },
    { to: "/release-gate", icon: Gauge, label: "Final All-Phase Audit" },
    { to: "/dependency-map", icon: Network, label: "Feature Dependency Map" },
  ] },
  { label: "Advanced (Phase 3)", items: [
    { to: "/estate-assistant", icon: Sparkles, label: "AI Estate Assistant" },
    { to: "/estate-value", icon: Wallet, label: "Estate Value" },
    { to: "/entity-hub", icon: Building2, label: "Entity Hub" },
    { to: "/compliance-calendar", icon: CalendarClock, label: "Compliance Calendar" },
    { to: "/time-capsule", icon: Clock, label: "Time Capsule" },
    { to: "/signatures", icon: PenLine, label: "Signature Requests" },
    { to: "/family-governance", icon: Landmark, label: "Family Governance" },
    { to: "/business-succession", icon: Network, label: "Business Succession" },
    { to: "/next-gen-training", icon: GraduationCap, label: "Next-Gen Training" },
  ] },
  { label: "Convene", items: [
    { to: "/meetings", icon: Users, label: "Meeting Suite" },
  ]},
  { label: "Capital", items: [
    { to: "/funding", icon: Landmark, label: "Funding Center" },
    { to: "/funding-radar", icon: Radar, label: "Funding Radar (live)" },
    { to: "/applications", icon: FileText, label: "Applications" },
    { to: "/grants-explorer", icon: Search, label: "Grants Explorer" },
    { to: "/rates", icon: Percent, label: "Rates Room" },
    { to: "/market-data", icon: LineChart, label: "Market Data" },
  ]},
  { label: "Studio", items: [
    { to: "/studio", icon: PenTool, label: "Instrument Studio" },
    { to: "/drafting", icon: FileText, label: "Guided Drafting" },
    { to: "/clause-library", icon: BookOpen, label: "Clause Library" },
    { to: "/bookkeeping", icon: Receipt, label: "Bookkeeping Drafting" },
    { to: "/maps", icon: MapIcon, label: "Maps" },
  ]},
  { label: "Academy", items: [
    { to: "/academy/home", icon: Home, label: "Academy Home" },
    { to: "/academy", icon: GraduationCap, label: "Courses" },
    { to: "/academy/transcript", icon: ScrollText, label: "Transcript" },
    { to: "/academy/badges", icon: ShieldCheck, label: "Badges" },
    { to: "/academy/roles", icon: Users, label: "Academic Roles" },
    { to: "/academy/qa", icon: Gauge, label: "QA & Accreditation" },
    { to: "/academy/practicum-inbox", icon: ClipboardCheck, label: "Practicum Inbox", admin: true },
    { to: "/academy/integrity", icon: Scale, label: "Integrity Cases", admin: true },
    { to: "/academy/teaching", icon: Sparkles, label: "Teaching Generator", admin: true },
    { to: "/academy/verify", icon: Search, label: "Verify Transcript" },
  ]},
  { label: "Intelligence", items: [
    { to: "/library", icon: BookOpen, label: "Live Library" },
    { to: "/lookup", icon: Search, label: "Entity Lookup" },
    { to: "/agencies", icon: Landmark, label: "Gov Agencies" },
    { to: "/tax-resources", icon: Receipt, label: "Tax Resources" },
    { to: "/regulatory", icon: Newspaper, label: "Regulatory Watch" },
    { to: "/registers", icon: HelpCircle, label: "Governance Registers" },
    { to: "/holidays", icon: CalendarDays, label: "Calendar" },
  ]},
  { label: "System", items: [
    { to: "/connect", icon: Link2, label: "Connect (M365)" },
    { to: "/profile", icon: User, label: "Profile & Alerts" },
    { to: "/interface-mode", icon: Compass, label: "Interface Mode" },
    { to: "/backup", icon: DatabaseBackup, label: "Backup & Restore" },
    { to: "/integrity", icon: ClipboardCheck, label: "Integrity Scan" },
    { to: "/users", icon: Users, label: "Users" },
  ]},
];

// Guided mode shows only these everyday-essential routes (plus /interface-mode).
const GUIDED_ROUTES = new Set([
  "/me", "/dashboard", "/my-root", "/council", "/vault", "/media", "/academy",
  "/estate-assistant", "/reminders", "/notifications", "/people", "/digital-assets",
  "/physical-assets", "/legacy-messages", "/final-wishes", "/healthcare-directives",
  "/time-capsule", "/safety", "/profile", "/interface-mode",
]);

export default function Shell() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiOpen, setAiOpen] = useState(false);
  const [scope, setScope] = useState(() => localStorage.getItem("ehu_scope") || "personal");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [uiMode, setUiMode] = useState(() => localStorage.getItem("ehu_ui_mode") || "advanced");
  const nav = useNavigate();

  useEffect(() => {
    me().then(r => { setUser(r.data); if (r.data.ui_mode) { setUiMode(r.data.ui_mode); localStorage.setItem("ehu_ui_mode", r.data.ui_mode); } })
      .catch(() => setUser(null)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const sync = () => setUiMode(localStorage.getItem("ehu_ui_mode") || "advanced");
    window.addEventListener("ehu-ui-mode", sync);
    window.addEventListener("storage", sync);
    return () => { window.removeEventListener("ehu-ui-mode", sync); window.removeEventListener("storage", sync); };
  }, []);

  if (loading) return <div className="p-10 text-gray-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const signout = () => { localStorage.removeItem("ehu_token"); nav("/login"); };
  const setScopePersist = (s, go) => {
    setScope(s); localStorage.setItem("ehu_scope", s); setMobileOpen(false);
    if (go) nav(s === "collective" ? "/dashboard" : "/me");
  };
  const canCollective = user.role === "records_officer" || user.is_admin || (user.portal_role || "").includes("Trustee");
  const toggleUiMode = async () => {
    const next = uiMode === "guided" ? "advanced" : "guided";
    setUiMode(next); localStorage.setItem("ehu_ui_mode", next);
    try { await apiSetUiMode(next); } catch (e) {}
    setMobileOpen(false);
  };
  const groupsForMode = uiMode === "guided"
    ? groups.map(g => ({ ...g, items: g.items.filter(it => GUIDED_ROUTES.has(it.to)) })).filter(g => g.items.length)
    : groups;
  const visibleGroups = groupsForMode.filter(g => !g.scope || g.scope === "both" || g.scope === scope);

  const sidebar = (
    <>
      <div className="px-3 pb-3 border-b border-white/10 mb-2 flex items-center justify-between">
        <div>
          <div className="font-display text-lg leading-tight" style={{color: '#F4F1EA'}}>Endigenum Hereditas</div>
          <div className="text-xs" style={{color: '#C9A050'}}>Apex Registry</div>
        </div>
        <button className="lg:hidden text-white/70" onClick={() => setMobileOpen(false)} data-testid="sidebar-close" aria-label="Close navigation"><X size={18} aria-hidden="true"/></button>
      </div>
      <div className="px-3 pb-3 mb-2 border-b border-white/10" data-testid="dashboard-switcher">
        <div className="text-xs mb-1" style={{color:'#B8B0A0'}}>Dashboard {scope === "collective" ? "2 · Collective" : "1 · Personal"}</div>
        <div className="flex gap-1" role="group" aria-label="Dashboard scope">
          <button onClick={() => setScopePersist("personal", true)} data-testid="scope-personal" aria-pressed={scope==="personal"}
            className="flex-1 text-xs py-1 rounded" style={{background: scope==="personal" ? "#C9A050" : "rgba(255,255,255,0.06)", color: scope==="personal" ? "#1B3B2B" : "#D6CFB8"}}>Personal</button>
          <button onClick={() => canCollective ? setScopePersist("collective", true) : null} data-testid="scope-collective" disabled={!canCollective} aria-pressed={scope==="collective"}
            title={canCollective ? "" : "Requires collective access"}
            className="flex-1 text-xs py-1 rounded" style={{background: scope==="collective" ? "#C9A050" : "rgba(255,255,255,0.06)", color: scope==="collective" ? "#1B3B2B" : (canCollective ? "#D6CFB8" : "#8A8270"), cursor: canCollective ? "pointer" : "not-allowed"}}>Collective</button>
        </div>
      </div>
      <div className="px-3 pb-3 mb-2 border-b border-white/10" data-testid="ui-mode-switcher">
        <div className="text-xs mb-1" style={{color:'#B8B0A0'}}>Interface: {uiMode === "guided" ? "Guided" : "Advanced"}</div>
        <button onClick={toggleUiMode} data-testid="ui-mode-toggle" aria-label={`Switch to ${uiMode === "guided" ? "Advanced" : "Guided"} interface mode`}
          className="w-full text-xs py-1 rounded flex items-center justify-center gap-1"
          style={{background: "rgba(255,255,255,0.06)", color: "#D6CFB8"}}>
          <Compass size={12} aria-hidden="true"/> Switch to {uiMode === "guided" ? "Advanced" : "Guided"}
        </button>
      </div>
      {visibleGroups.map(g => (
        <div key={g.label}>
          <div className="nav-group-label" role="heading" aria-level="2">{g.label}</div>
          {g.items.map(it => (
            <NavLink key={it.to} to={it.to} data-testid={`nav-${it.to.slice(1)}`} onClick={() => setMobileOpen(false)}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
              <it.icon size={15} aria-hidden="true" /> {it.label}
            </NavLink>
          ))}
        </div>
      ))}
      <div className="mt-6 px-3 py-3 border-t border-white/10 text-xs" style={{color:'#B8B0A0'}}>
        <div style={{color:'#F4F1EA'}} className="font-medium">{user.name}</div>
        <div className="break-all">{user.email}</div>
        <div className="mt-1">{user.portal_role || (user.role === "records_officer" ? "Records Officer" : (user.is_admin ? "Trustee Administrator" : "Trustee"))}</div>
        <button onClick={signout} data-testid="signout-btn" className="mt-3 flex items-center gap-1 hover:text-white" aria-label="Sign out">
          <LogOut size={13} aria-hidden="true" /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen">
      <a href="#main-content" className="sr-only skip-link" data-testid="skip-to-content">Skip to main content</a>
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 h-14 grove-sidebar" data-testid="mobile-topbar">
        <button onClick={() => setMobileOpen(true)} data-testid="sidebar-open" style={{color:'#F4F1EA'}} aria-label="Open navigation" aria-expanded={mobileOpen} aria-controls="primary-sidebar"><Menu size={22} aria-hidden="true"/></button>
        <div className="font-display text-base" style={{color:'#F4F1EA'}}>Endigenum Hereditas</div>
        <div className="text-xs" style={{color:'#C9A050'}}>{scope === "collective" ? "Collective" : "Personal"}</div>
      </div>
      {/* Mobile drawer backdrop */}
      {mobileOpen && <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setMobileOpen(false)} data-testid="sidebar-backdrop" aria-hidden="true" />}
      {/* Sidebar: drawer on mobile, sticky on desktop */}
      <aside data-testid="sidebar" id="primary-sidebar" aria-label="Primary navigation"
        className={`grove-sidebar w-72 lg:w-64 shrink-0 py-4 px-2 overflow-y-auto z-50 fixed lg:sticky top-0 h-screen transition-transform duration-200 ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <nav aria-label="Primary">{sidebar}</nav>
      </aside>
      <main id="main-content" tabIndex="-1" className="flex-1 min-w-0 fade-in pt-14 lg:pt-0">
        <div className="max-w-[1440px] mx-auto p-4 sm:p-6 lg:p-10">
          <Outlet />
        </div>
      </main>
      <button onClick={() => setAiOpen(true)} data-testid="ai-fab" aria-label="Open AI assistant"
        className="fixed bottom-6 right-4 sm:right-6 rounded-full shadow-lg flex items-center gap-2 px-4 py-3 z-40"
        style={{background: 'var(--gold)', color: '#1A1A1A'}}>
        <Sparkles size={16} aria-hidden="true"/> <span className="hidden sm:inline">Ask AI</span>
      </button>
      {aiOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label="AI Assistant">
          <div className="w-full sm:w-[520px] bg-white h-full overflow-y-auto border-l" style={{borderColor:'var(--border)'}}>
            <div className="flex justify-between items-center p-4 border-b" style={{borderColor:'var(--border)'}}>
              <div className="font-display text-xl">AI Assistant</div>
              <button data-testid="ai-close" onClick={() => setAiOpen(false)} aria-label="Close AI assistant"><X size={18} aria-hidden="true"/></button>
            </div>
            <AiPanel />
          </div>
        </div>
      )}
    </div>
  );
}
